import { useMemo } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Loader } from '@opendoor/atoms/loader';
import type { MarketplaceBrowseQuery } from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import { centsToDollars } from 'components/marketplace/helpers/money';
import type { MarketplaceFavoriteChangeHandler } from 'components/marketplace/helpers/useMarketplaceFavorite';
import { toDwellingType } from 'components/marketplace/property/dwellingType';
import { ListingCard, type PhotoLoadMode } from 'components/marketplace/property/SimilarListings';
import type { ListingCardData } from 'components/marketplace/property/types';
import type { LikedPropertyIds } from 'components/marketplace/types';

type MarketplacePropertyList = MarketplaceBrowseQuery['marketplace']['listings'];
type MarketplacePropertyListItem = MarketplacePropertyList['listings'][number];

const listingCardData = (
  listing: MarketplacePropertyListItem,
  likedPropertyIds?: LikedPropertyIds,
): ListingCardData => ({
  addressToken: listing.addressId,
  baths: listing.bathrooms,
  beds: listing.bedrooms,
  city: listing.city,
  dwellingType: toDwellingType(listing.dwellingType),
  fullAddress: listing.fullAddress,
  listPrice: centsToDollars(listing.listPriceCents),
  livingSquareFootage: listing.totalLivingSquareFootage,
  neighborhood: listing.neighborhood,
  photos: listing.photoUrls,
  isLiked: likedPropertyIds == null ? null : likedPropertyIds.has(listing.addressId),
  slug: listing.slug,
  state: listing.state,
  street: listing.street,
});

interface PropertyListProps {
  isLoading: boolean;
  isLoadingMore: boolean;
  handleOnSeeMore: () => void;
  listings?: MarketplacePropertyList;
  likedPropertyIds?: LikedPropertyIds;
  initialFavoritesPending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  firstPhotoLoadMode: PhotoLoadMode;
}

export const PropertyList = ({
  isLoading,
  isLoadingMore,
  handleOnSeeMore,
  listings,
  likedPropertyIds,
  initialFavoritesPending,
  onFavoriteChange,
  firstPhotoLoadMode,
}: PropertyListProps) => {
  const propertyCards = useMemo(() => {
    return listings?.listings.map((listing, index) => {
      return (
        <Box key={listing.addressId}>
          <ListingCard
            listing={listingCardData(listing, likedPropertyIds)}
            favoriteOrigin="GALLERY"
            initialFavoritePending={initialFavoritesPending}
            onFavoriteChange={onFavoriteChange}
            photoLoadMode={index === 0 ? firstPhotoLoadMode : 'deferred'}
          />
        </Box>
      );
    });
  }, [listings, likedPropertyIds, initialFavoritesPending, onFavoriteChange, firstPhotoLoadMode]);

  return (
    <>
      {isLoading ? (
        <Row className="w-full items-center justify-center">
          <Loader size="lg" />
        </Row>
      ) : (
        <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">{propertyCards}</Box>
      )}
      {listings?.nextPage != null && (
        <Box className="pb-8">
          <Button
            analyticsName="more-property-list"
            aria-label="Show more"
            onClick={handleOnSeeMore}
            variant="secondary"
            loading={isLoadingMore}
            className="w-full"
          >
            Show more
          </Button>
        </Box>
      )}
    </>
  );
};
